import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, CircularProgress, Alert, Stack
} from '@mui/material';
import api from '../../api';

/**
 * SoloStart
 *
 * Robust version:
 * - essaie plusieurs endpoints pour récupérer la liste des élèves d'une classe
 * - tolère les erreurs 404/endpoint manquant et permet la saisie manuelle du nom
 * - construit un objet `match` attendu par ResultatSoloBoxe et navigue vers la page récap via state
 *
 * Query params supportés (optionnel) :
 * - ?classeId=...  (charge automatiquement la liste d'élèves si l'endpoint existe)
 * - ?activity=...  (pré-remplit l'activité)
 */

export default function SoloStart() {
  const location = useLocation();
  const navigate = useNavigate();
  const q = new URLSearchParams(location.search);
  const classeId = q.get('classeId') || '';
  const activity = q.get('activity') || 'Boxe Française';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eleves, setEleves] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [manualName, setManualName] = useState('');
  const [points, setPoints] = useState('');
  const [arbitreId, setArbitreId] = useState(''); // optional

  useEffect(() => {
    let mounted = true;

    async function tryFetchEleves(classeId) {
      if (!classeId) return [];
      const attempts = [
        `/classes/${encodeURIComponent(classeId)}/eleves`,
        `/eleves?classeId=${encodeURIComponent(classeId)}`,
        `/eleves?classe=${encodeURIComponent(classeId)}`
      ];
      for (const url of attempts) {
        try {
          const res = await api.get(url);
          if (!res || !res.data) continue;
          // support many response shapes
          if (Array.isArray(res.data)) return res.data;
          if (res.data && Array.isArray(res.data.data)) return res.data.data;
          if (res.data && Array.isArray(res.data.items)) return res.data.items;
          // sometimes API returns object keyed by results
          if (res.data && typeof res.data === 'object') {
            // try to find first array property
            const arrProp = Object.keys(res.data).find(k => Array.isArray(res.data[k]));
            if (arrProp) return res.data[arrProp];
          }
          // otherwise ignore and try next
        } catch (err) {
          // If 404, try next endpoint; for other errors, log and try next as well
          if (err?.response?.status === 404) {
            // continue to next attempt
            // eslint-disable-next-line no-console
            console.warn(`SoloStart: ${url} -> 404, trying next endpoint`);
            continue;
          }
          // eslint-disable-next-line no-console
          console.warn(`SoloStart: error fetching ${url}:`, err?.message || err);
          continue;
        }
      }
      return [];
    }

    (async () => {
      if (!classeId) return;
      setLoading(true);
      setError('');
      try {
        const list = await tryFetchEleves(classeId);
        if (!mounted) return;
        setEleves(list || []);
        if (!list || list.length === 0) {
          setError('Aucun élève récupéré pour cette classe — vous pouvez saisir un nom manuellement.');
        } else {
          setError('');
        }
      } catch (err) {
        if (!mounted) return;
        // eslint-disable-next-line no-console
        console.error('SoloStart fetchEleves error', err);
        setError('Erreur lors du chargement des élèves.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [classeId]);

  const startResult = () => {
    // validation: either selectedId or manualName must be provided
    if (!selectedId && !manualName) {
      setError('Sélectionnez un élève ou saisissez un nom.');
      return;
    }

    const match = {
      params: {
        classeId: classeId || null,
        activity: activity,
        classeName: undefined
      },
      soloBoxerId: selectedId || null,
      soloBoxerName: selectedId ? undefined : manualName || undefined,
      soloBoxerPoints: points !== '' ? Number(points) : null,
      arbitreId: arbitreId || null,
      createdAt: new Date().toISOString()
    };

    navigate('/apps/juge-boxe/resultat-solo', { state: { match } });
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 900, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Nouveau jugement — 1 boxeur</Typography>
        <Typography variant="subtitle2" sx={{ color: '#666', mb: 2 }}>
          Activité : {activity} {classeId ? `• Classe : ${classeId}` : ''}
        </Typography>

        {loading && <Box sx={{ textAlign: 'center', my: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          <TextField
            select
            label="Choisir un élève (ou laisser vide pour saisie manuelle)"
            value={selectedId}
            onChange={e => { setSelectedId(e.target.value); setManualName(''); setError(''); }}
            size="small"
            fullWidth
          >
            <MenuItem value="">— Aucun —</MenuItem>
            {eleves.map(e => (
              <MenuItem key={e._id ?? e.id ?? `${e.nom}-${e.prenom}`} value={e._id ?? e.id}>
                {`${e.prenom ? e.prenom + ' ' : ''}${e.nom ?? e.name ?? e.label ?? (e._id ?? e.id)}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Ou saisir le nom complet (si pas dans la liste)"
            value={manualName}
            onChange={e => { setManualName(e.target.value); if (e.target.value) setSelectedId(''); setError(''); }}
            size="small"
            fullWidth
          />

          <TextField
            label="Points attribués"
            value={points}
            onChange={e => setPoints(e.target.value.replace(/[^\d]/g, ''))}
            size="small"
            fullWidth
          />

          <TextField
            label="Arbitre (optionnel - id)"
            value={arbitreId}
            onChange={e => setArbitreId(e.target.value)}
            size="small"
            helperText="Si vous avez l'ID de l'arbitre, vous pouvez le saisir pour incrémenter son compteur"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={startResult}>Voir le récapitulatif</Button>
            <Button variant="outlined" onClick={() => navigate(-1)}>Annuler</Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}